"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NetworkQualityObserver = void 0;
const logger_1 = require("@streem/logger");
const streem_logger_1 = require("../util/streem_logger");
const toolbox_1 = require("@streem/toolbox");
const remote_video_quality_score_manager_1 = require("../managers/remote_video_quality_score_manager");
const mos_quality_score_manager_1 = require("../managers/mos_quality_score_manager");
const mos_quality_score_utils_1 = require("../util/mos_quality_score_utils");
class NetworkQualityObserver {
    constructor({ roomWebRtcStats, isLocalVideoTrackEnabled, isLocalAudioTrackEnabled, isRemoteVideoTrackEnabled, isRemoteVideoTrackBackgrounded, remoteSignalStrength, onLocalNetworkQualityChange, onRemoteNetworkQualityChange, }) {
        this.logger = streem_logger_1.streemLogger.extend('sdk-chime-video:NetworkQualityObserver');
        this.subscriptionManager = new toolbox_1.SubscriptionManager();
        this.mediaEnabledState = {
            localVideo: true,
            localAudio: true,
            remoteVideo: true,
        };
        this.onWebRTCStats = (webRTCStats) => {
            if (!this.mediaEnabledState.localVideo && !this.mediaEnabledState.localAudio) {
                this.logger.info('Local video and audio are disabled, so network quality will not be calculated');
                this.previousStats = undefined;
                this.previousLocalNetworkQuality = undefined;
                this.previousRemoteNetworkQuality = undefined;
                return;
            }
            const { remoteNetworkQuality, localNetworkQuality } = this.calculateNetworkQualityFromWebRTCStats(webRTCStats);
            // remoteNetworkQuality and localNetworkQuality are undefined if there are no previous stats
            //     or some of the required current stats (from AWS Chime) are missing
            if (typeof remoteNetworkQuality === 'number') {
                this.logger.debug('remoteNetworkQuality', remoteNetworkQuality);
                this.onRemoteNetworkQualityChange(remoteNetworkQuality);
            }
            if (typeof localNetworkQuality === 'number') {
                this.logger.debug('localNetworkQuality', localNetworkQuality);
                this.onLocalNetworkQualityChange(localNetworkQuality);
            }
            this.logNetworkQualityScoreOnChange(localNetworkQuality, remoteNetworkQuality);
        };
        this.subscriptionManager.add(roomWebRtcStats.subscribe(this.onWebRTCStats));
        this.subscriptionManager.add(isLocalVideoTrackEnabled.subscribe(enabled => (this.mediaEnabledState.localVideo = enabled)));
        this.subscriptionManager.add(isLocalAudioTrackEnabled.subscribe(enabled => (this.mediaEnabledState.localAudio = enabled)));
        this.subscriptionManager.add(remoteSignalStrength.subscribe(signalStrength => (this.remoteSignalStrength = signalStrength)));
        this.subscriptionManager.add(isRemoteVideoTrackEnabled.subscribe(enabled => (this.mediaEnabledState.remoteVideo = enabled)));
        this.subscriptionManager.add(isRemoteVideoTrackBackgrounded.subscribe(backgrounded => (this.isRemoteVideoBackgrounded = backgrounded)));
        this.remoteVideoQualityScoreMananger = new remote_video_quality_score_manager_1.RemoteVideoQualityScoreManager(this.logger);
        this.mosQualityScoreManager = new mos_quality_score_manager_1.MosQualityScoreManager(this.logger);
        this.onLocalNetworkQualityChange = onLocalNetworkQualityChange;
        this.onRemoteNetworkQualityChange = onRemoteNetworkQualityChange;
    }
    disconnect() {
        this.subscriptionManager.unsubscribeAll();
    }
    calculateNetworkQualityFromWebRTCStats(webRTCStats) {
        if (!this.previousStats) {
            this.logger.info('No previous stats exist, so network quality will not be calculated');
            this.previousStats = this.convertWebRTCStatsToNQSStats(webRTCStats);
            this.logger.debug('currentStats', this.previousStats);
            return {};
        }
        const currentStats = this.convertWebRTCStatsToNQSStats(webRTCStats);
        const localMosScore = this.mosQualityScoreManager.calculateScore('local', currentStats, this.previousStats);
        let remoteMosScore = undefined;
        let remoteVideoQualityScore = undefined;
        // Note that this assumes a single remote participant.  It seems like this assumption is baked into our
        //     current UX around signal strength, so it should be fine for now, but this would need to be revisited
        //     if we move to a multiple remote participants.
        if (this.isVideoAvailableForRemoteUser(currentStats, this.previousStats)) {
            this.logger.debug('remote user disconnected so no remote NWS');
            remoteMosScore = 0;
        }
        else {
            remoteMosScore = this.mosQualityScoreManager.calculateScore('remote', currentStats, this.previousStats);
            remoteVideoQualityScore = this.remoteVideoQualityScoreMananger.calculateScore(webRTCStats, this.mediaEnabledState.remoteVideo);
        }
        this.logger.debug('remote NQ scores', {
            remoteNetworkQualityScoreUsingMOS: remoteMosScore,
            remoteNetworkVideoQualityScore: remoteVideoQualityScore,
        });
        this.previousStats = currentStats;
        return {
            // use video quality score if available, otherwise use MOS
            remoteNetworkQuality: remoteVideoQualityScore !== null && remoteVideoQualityScore !== void 0 ? remoteVideoQualityScore : remoteMosScore,
            localNetworkQuality: localMosScore,
        };
    }
    convertWebRTCStatsToNQSStats(webRTCStats) {
        var _a, _b, _c, _d;
        // only use local audio and video stats if they are enabled
        const localAudioTrackStats = this.mediaEnabledState.localAudio && webRTCStats.localAudioTrackStats.length > 0
            ? webRTCStats.localAudioTrackStats[0]
            : undefined;
        const localVideoTrackStats = this.mediaEnabledState.localVideo && webRTCStats.localVideoTrackStats.length > 0
            ? webRTCStats.localVideoTrackStats[0]
            : undefined;
        const remoteAudioTrackStats = webRTCStats.remoteAudioTrackStats.length > 0
            ? webRTCStats.remoteAudioTrackStats[0]
            : undefined;
        const remoteVideoTrackStats = webRTCStats.remoteVideoTrackStats.length > 0
            ? webRTCStats.remoteVideoTrackStats[0]
            : undefined;
        // roundTripTime only captured for local tracks
        const roundTripTimeMs = (0, mos_quality_score_utils_1.findMaxOrUndefined)(localAudioTrackStats === null || localAudioTrackStats === void 0 ? void 0 : localAudioTrackStats.roundTripTime, localVideoTrackStats === null || localVideoTrackStats === void 0 ? void 0 : localVideoTrackStats.roundTripTime);
        const localPacketsLostPercent = (0, mos_quality_score_utils_1.findMaxOrUndefined)(localAudioTrackStats === null || localAudioTrackStats === void 0 ? void 0 : localAudioTrackStats.packetLossPercentage, // packetsLost from AWS Chime is a percentage
        localVideoTrackStats === null || localVideoTrackStats === void 0 ? void 0 : localVideoTrackStats.packetLossPercentage);
        if (localPacketsLostPercent === undefined) {
            this.logger.debug('localPacketsLostPercent is undefined', {
                localAudioTrackStats,
                localVideoTrackStats,
            });
        }
        const localJitterMs = (0, mos_quality_score_utils_1.findMaxOrUndefined)(localAudioTrackStats === null || localAudioTrackStats === void 0 ? void 0 : localAudioTrackStats.jitter, localVideoTrackStats === null || localVideoTrackStats === void 0 ? void 0 : localVideoTrackStats.jitter);
        const remotePacketsLostPercent = (0, mos_quality_score_utils_1.findMaxOrUndefined)(remoteAudioTrackStats === null || remoteAudioTrackStats === void 0 ? void 0 : remoteAudioTrackStats.packetLossPercentage, remoteVideoTrackStats === null || remoteVideoTrackStats === void 0 ? void 0 : remoteVideoTrackStats.packetLossPercentage);
        if (remotePacketsLostPercent === undefined) {
            this.logger.debug('remotePacketsLostPercent is undefined', {
                remoteAudioTrackStats,
                remoteVideoTrackStats,
            });
        }
        const remoteJitterMs = (0, mos_quality_score_utils_1.findMaxOrUndefined)(remoteAudioTrackStats === null || remoteAudioTrackStats === void 0 ? void 0 : remoteAudioTrackStats.jitter, remoteVideoTrackStats === null || remoteVideoTrackStats === void 0 ? void 0 : remoteVideoTrackStats.jitter);
        return {
            roundTripTimeMs,
            local: {
                packetsLostPercent: localPacketsLostPercent,
                jitterMs: localJitterMs,
                frameRate: (_a = localVideoTrackStats === null || localVideoTrackStats === void 0 ? void 0 : localVideoTrackStats.frameRate) !== null && _a !== void 0 ? _a : undefined,
                packetsSent: (_b = localVideoTrackStats === null || localVideoTrackStats === void 0 ? void 0 : localVideoTrackStats.packetsSent) !== null && _b !== void 0 ? _b : undefined,
            },
            remote: {
                packetsLostPercent: remotePacketsLostPercent,
                jitterMs: remoteJitterMs,
                frameRate: (_c = remoteVideoTrackStats === null || remoteVideoTrackStats === void 0 ? void 0 : remoteVideoTrackStats.frameRate) !== null && _c !== void 0 ? _c : undefined,
                packetsReceived: (_d = remoteVideoTrackStats === null || remoteVideoTrackStats === void 0 ? void 0 : remoteVideoTrackStats.packetsReceived) !== null && _d !== void 0 ? _d : 0,
            },
        };
    }
    logNetworkQualityScoreOnChange(currentLocalNetworkQuality, currentRemoteNetworkQuality) {
        if (currentLocalNetworkQuality != null &&
            currentLocalNetworkQuality !== this.previousLocalNetworkQuality) {
            this.previousLocalNetworkQuality = currentLocalNetworkQuality;
            this.logger.setContextValue(logger_1.contextValues.CHIME_LOCAL_NETWORK_QUALITY, currentLocalNetworkQuality);
            if (currentLocalNetworkQuality < 2) {
                this.logger.warn(`Chime local participant network quality is low: ${currentLocalNetworkQuality}`);
            }
            else {
                this.logger.info(`Chime local participant network quality: ${currentLocalNetworkQuality}`);
            }
            this.logger.removeContextValue(logger_1.contextValues.CHIME_LOCAL_NETWORK_QUALITY);
        }
        if (currentRemoteNetworkQuality != null &&
            currentRemoteNetworkQuality !== this.previousRemoteNetworkQuality) {
            this.previousRemoteNetworkQuality = currentRemoteNetworkQuality;
            this.logger.setContextValue(logger_1.contextValues.CHIME_REMOTE_NETWORK_QUALITY, currentRemoteNetworkQuality);
            if (currentRemoteNetworkQuality < 2) {
                this.logger.warn(`Chime remote participant network quality is low: ${currentRemoteNetworkQuality}`);
            }
            else {
                this.logger.info(`Chime remote participant network quality: ${currentRemoteNetworkQuality}`);
            }
            this.logger.removeContextValue(logger_1.contextValues.CHIME_REMOTE_NETWORK_QUALITY);
        }
    }
    isVideoAvailableForRemoteUser(currentStats, previousStats) {
        // Per AWS recommendation, use audio signalStrength from
        //    https://aws.github.io/amazon-chime-sdk-js/interfaces/realtimecontroller.html#realtimesubscribetovolumeindicator
        //    They said that it would work even if remote video was muted, but they are wrong, so we also check
        //    for packetsReceived === 0 for 2 consecutive periods
        const packetsReceivedZero = this.mediaEnabledState.remoteVideo &&
            currentStats.remote.packetsReceived === 0 &&
            previousStats.remote.packetsReceived === 0;
        return (!this.isRemoteVideoBackgrounded &&
            (this.remoteSignalStrength === 0 || packetsReceivedZero));
    }
}
exports.NetworkQualityObserver = NetworkQualityObserver;
//# sourceMappingURL=network_quality_observer.js.map